import { useQSet } from '@exam-management/common/qset-provider';
import { QSetStatus } from '@exam-management/graphql';
import {
  QuestionChangeButton,
  QuestionChangeModalProvider,
} from '@exam-management/qset-question-change';
import { QSetQuestionDetails } from '@exam-management/qset-question-details';
import { Flex, InfiniteScroll, Loading, Paper } from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { useEffect } from 'react';

import { CHANGE_QSET_QUESTION_REQUIRED_PERMISSION } from '../../constants';
import {
  useGetInfiniteQSetQuestionDetailsList,
  useGetPaginatedQSetQuestionsDetailsList,
  useQSetQuestionListSearchParams,
} from '../../hooks';

export default function QSetQuestionWithQuestionList() {
  const { qSet } = useQSet();
  const [{ page, pageSize }, { getPageSizeAsNumber }] = useQSetQuestionListSearchParams();
  const pageSizeAsNumber = getPageSizeAsNumber(pageSize);
  const isInfiniteMode = pageSize === 'All';
  const hasChangeQuestionPermission = useHasPermission(CHANGE_QSET_QUESTION_REQUIRED_PERMISSION);
  const hasChangeQuestion = qSet.status === QSetStatus.Draft && hasChangeQuestionPermission;

  // ***** IMPORTANT NOTE *****
  // these 2 hooks are fetching just once because it had the same variable state
  // if you want to change initial variable in one of them, it'll be over-fetching, please take a risk
  const {
    data: infiniteQSetQuestionsData,
    loading: isInfiniteQSetQuestionsLoading,
    fetchMoreLoading: isFetchingMoreInfiniteQSetQuestions,
    refetch: refetchInfiniteQSetQuestions,
    fetchMore: fetchMoreInfiniteQSetQuestions,
    hasMore,
  } = useGetInfiniteQSetQuestionDetailsList({
    qSetId: qSet.id,
    pageSize: pageSizeAsNumber,
  });

  const {
    data: paginatedQSetQuestionsData,
    loading: isPaginatedQSetQuestionsLoading,
    refetch: refetchPaginatedQSetQuestions,
  } = useGetPaginatedQSetQuestionsDetailsList({
    page,
    pageSize: pageSizeAsNumber,
    qSetId: qSet.id,
  });

  useEffect(() => {
    if (isInfiniteMode) {
      refetchInfiniteQSetQuestions();
    } else {
      refetchPaginatedQSetQuestions();
    }
  }, [pageSize]);

  const qSetQuestions = isInfiniteMode ? infiniteQSetQuestionsData : paginatedQSetQuestionsData;
  const refetch = isInfiniteMode ? refetchInfiniteQSetQuestions : refetchPaginatedQSetQuestions;
  const isLoading = isInfiniteQSetQuestionsLoading || isPaginatedQSetQuestionsLoading;

  if (isLoading) {
    return <Loading sx={{ height: '100%' }} />;
  }

  const content = (
    <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(6) }}>
      {qSetQuestions.map((qSetQuestion) => {
        const { id, question, order } = qSetQuestion;

        return (
          <QuestionChangeModalProvider
            questionId={question.id}
            qSetQuestionId={id}
            questionGroupSize={question.questionGroup?.size}
            onComplete={refetch}
            key={id}
          >
            <Paper>
              <QSetQuestionDetails
                question={question}
                order={order}
                endAdornment={
                  hasChangeQuestion ? (
                    <QuestionChangeButton questionType={question.questionGroup?.type} />
                  ) : undefined
                }
              />
            </Paper>
          </QuestionChangeModalProvider>
        );
      })}
    </Flex>
  );

  return isInfiniteMode ? (
    <InfiniteScroll
      hasMore={hasMore}
      handleFetch={fetchMoreInfiniteQSetQuestions}
      loading={isFetchingMoreInfiniteQSetQuestions}
      loader={<Loading />}
      scrollThreshold={0.7}
      scrollableTarget='frame-content'
    >
      {content}
    </InfiniteScroll>
  ) : (
    content
  );
}
